import React, { FC } from "react";
import styled from "styled-components";
import { marked } from "marked";
import { Entry } from "contentful";

interface QuoteBlockProps extends Entry {
  fields: {
    content?: string;
    name?: string;
    title?: string;
    image?: {
      fields: {
        file: {
          url: string;
        };
      };
    };
    altText?: string;
    imageMaxWidth?: number;
    linkUrl?: string;
  }
  parentProps: {
    carouselTheme: string;
    activeSlideStyling?: string;
  }
}

const Container = styled.div<{ $theme: string; $activeSlideStyling?: string }>`
  display: flex;
  padding: 1.75em 2rem 1.5em;
  max-width: 600px;
  min-height: 248px;
  margin: 0 auto;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  background: ${({ $theme }) => $theme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
  border-radius: 16px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  transition: all 0.3s ease-in-out;
  color: ${({ $theme }) => $theme === 'dark' ? '#ffffff' : '#121212'};
`;

const QuoteContent = styled.div<{ $theme: string }>`
  font-size: 1.2em;
  line-height: 1.5;
  transition: all 0.3s ease-in-out;
  color: ${({ $theme }) => $theme === 'dark' ? '#ffffff' : '#121212'};

  p {
    color: ${({ $theme }) => $theme === 'dark' ? '#ffffff' : '#121212'};
  }
`;

const HorizontalLine = styled.hr<{ $theme: string }>`
  width: 100%;
  border: 0;
  height: 1px;
  background: ${({ $theme }) => $theme === 'dark' ? '#cccccc33' : '#00000033'};
  margin: 20px 0 10px;
`;

const Attribution = styled.div`
  display: flex;
  align-items: center;
`;

const NameTitle = styled.div`
  margin-left: 15px;
  text-align: left;
`;

const Name = styled.h3<{ $theme: string }>`
  font-weight: 600;
  color: ${({ $theme }) => $theme === 'dark' ? '#ffffff' : '#121212'};
  margin: 0;
`;

const Title = styled.p<{ $theme: string }>`
  font-size: 12px;
  margin: 0;
  color: ${({ $theme }) => $theme === 'dark' ? '#ffffff' : '#121212'};
`;

const Image = styled.img<{ $maxWidth?: number }>`
  max-width: ${({ $maxWidth }) => $maxWidth ? `${$maxWidth}px` : '100px'};
  max-height: 4rem;
`;

const QuoteBlock: FC<QuoteBlockProps> = ({ 
  fields: {
    content,
    name,
    title,
    image,
    altText,
    imageMaxWidth,
    linkUrl
  }, 
  parentProps: {
    carouselTheme,
    activeSlideStyling
  }
}) => {
  return (
    <>
      <Container $theme={carouselTheme} $activeSlideStyling={activeSlideStyling}>
        {content && (
          <QuoteContent $theme={carouselTheme} dangerouslySetInnerHTML={{ __html: marked.parse(content) }} />
        )}
        <HorizontalLine $theme={carouselTheme} className="hide-on-inactive" />
        <Attribution>
          {image?.fields.file.url && (
            <Image src={image.fields.file.url} alt={altText || "Logo"} $maxWidth={imageMaxWidth} />
          )}
          <NameTitle>
            {name && <Name $theme={carouselTheme}>{name}</Name>}
            {linkUrl && title ? (
              <a href={linkUrl} target="_blank" rel="noopener noreferrer">
                <Title $theme={carouselTheme}>{title}</Title>
              </a>
            ) : (
              title && <Title $theme={carouselTheme}>{title}</Title>
            )}
          </NameTitle>
        </Attribution>
      </Container>
    </>
  );
};

export default QuoteBlock;